import {createTheme} from '@mui/material/styles'
import {cyan} from '@mui/material/colors'

const palette = {
  primary: {main: '#86BC25'},
  secondary: cyan,
  errieBlack: '#1a1a1a',
  blackShade: '#222222',
  greyShade: 'rgba(255,255,255,0.87)',
  maroon: '#FB7272A1',
  orange: '#FD944BAD',
  green: '#C0FFB999',
  teal2: '#0D8390',
  text: {
    default: 'white'
  },
  divider: 'rgba(0, 0, 0, 0.12)',
  common: {
    teal3: '#6fc2b4',
    lightTeal: '#1e302f',
    black: '#000',
    black2: '#2C2C2C',
    black3: '#131314',
    black4: '#343434',
    black5: '#00008',
    white: '#fff',
    green: '#6fc2b4',
    blue: '#00a3e0',
    blue1: '#0983B1',
    lightBlue: '#496ABD',
    grey: '#808080',
    grey2: '#787878',
    grey24: '#3D3D3D',
    grey3: '#C6C4C4',
    grey4: '#009688',
    grey5: '#313131',
    grey6: '#BFBFBF',
    lightGrey: '#D9D9D9',
    darkGrey: '#8A8A8A',
    yellow: '#FAFF00',
    orange: '#FFA500',
    orange2: '#FA8E42',
    red: '#FF0000',
    successGreen: '#2e7d32',
    successGreen2: '#28a745'
  }
}
const typography = {
  h1: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '95.56px',
    letterSpacing: '-1.5px',
    lineHeight: '130px',
    fontWeight: 300
  },
  h2: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '59.72px',
    letterSpacing: '-0.5px',
    lineHeight: '81px',
    fontWeight: 300
  },
  h3: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '47.39px',
    lineHeight: '65px'
  },
  h4: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '33.57px',
    letterSpacing: '0.25px',
    lineHeight: '46px'
  },
  h5: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '23.69px',
    lineHeight: '32px'
  },
  h6: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '12px',
    letterSpacing: '0.25px',
    lineHeight: '27px'
  },
  body1: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    letterSpacing: '0.5px',
    lineHeight: '28px'
  },
  body2: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    letterSpacing: '0.25px',
    lineHeight: '20px'
  },
  subtitle1: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '15.8px',
    letterSpacing: '0.15px',
    lineHeight: '24px'
  },
  subtitle2: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '13.82px',
    letterSpacing: '0.1px',
    lineHeight: '24px'
  },
  button: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '13.82px',
    letterSpacing: '1.25px',
    lineHeight: '16px'
  },
  caption: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '11.85px',
    letterSpacing: '0.4px',
    lineHeight: '16px'
  },
  overline: {
    color: 'white',
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    fontSize: '11.85px',
    letterSpacing: '2px',
    lineHeight: '16px'
  },
  display: {
    display1: {
      fontFamily: 'Open Sans',
      fontSize: '56px',
      lineHeight: '64px',
      fontWeight: 400
    },
    display2: {
      fontFamily: 'Open Sans',
      fontSize: '48px',
      lineHeight: '56px',
      fontWeight: 400
    }
  },
  heading: {
    fontWeight: {
      regular: 400,
      semibold: 600,
      bold: 700,
      light: 100
    },
    lineHeight: {
      condensed: '12px',
      default: '16px',
      fluffy: '32px'
    },
    fontSize: {
      xxl: '40px',
      xl: '32px',
      l: '24px',
      m: '20px',
      s18: '18px',
      s: '16px',
      xs: '14px',
      xxs: '12px'
    }
  },
  body: {
    fontWeight: {
      regular: 400,
      semibold: 600
    },
    lineHeight: {
      condensed: '12px',
      default: '16px',
      fluffy: '32px',
      compact: '14.5px'
    },
    fontSize: {
      s: '16px',
      xs: '14px',
      xxs: '12px'
    }
  },
  caption2: {
    fontWeight: {
      regular: 400,
      default: 600
    },
    lineHeight: {
      condensed: '12px',
      default: '16px'
    },
    fontSize: {
      xxs: '12px'
    }
  }
}
const theme = createTheme({
  palette,
  typography,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: palette.blackShade
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: palette.common.white,
          minWidth: 0
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: palette.common.white,
          fontSize: '12px'
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: palette.common.white,
          fontSize: '12px'
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '.MuiTableCell-head': {
            color: palette.common.black,
            fontSize: '12px',
            fontWeight: 'bold'
          },
          '.MuiTableCell-body': {
            color: palette.common.white,
            fontSize: '12px'
          }
        }
      }
    }
  }
})

export default theme
